import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { AnalyticsContextSpaceContent } from '@gitbook/app-analytics/common';
import * as hiveCore from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';

import { NavigationLink } from '../../../navigation';

/**
 * Component to render the navigation between previous/next pages.
 */
export const PageSwitch = React.memo(function PageSwitchMemo(props: {
    space: api.Space;
    revision: hiveCore.Revision;
    pageId: string;
    analyticsContext: AnalyticsContextSpaceContent;
}): React.ReactElement | null {
    const { revision, pageId } = props;

    const { nextPage, previousPage } = React.useMemo(() => {
        const pages = listOrderedPages(revision);

        const pageIndex = pages.findIndex((x) => x.id === pageId);

        if (pageIndex === -1) {
            return { nextPage: null, previousPage: null };
        }

        return {
            nextPage: getPageClosestPageDocument(pages, pageIndex, 1),
            previousPage: getPageClosestPageDocument(pages, pageIndex, -1),
        };
    }, [revision, pageId]);

    const { previousLink, previousGroup, nextLink, nextGroup } = React.useMemo(() => {
        return {
            previousLink: previousPage
                ? {
                      screen: {
                          page: hiveCore.getAbsolutePagePathInRevision(revision, previousPage.id),
                      },
                  }
                : undefined,
            previousGroup: previousPage
                ? hiveCore.getPageParentInRevision(revision, previousPage.id)
                : null,
            nextLink: nextPage
                ? {
                      screen: {
                          page: hiveCore.getAbsolutePagePathInRevision(revision, nextPage.id),
                      },
                  }
                : undefined,
            nextGroup: nextPage ? hiveCore.getPageParentInRevision(revision, nextPage.id) : null,
        };
    }, [revision, nextPage, previousPage]);

    if (!nextPage && !previousPage) {
        return null;
    }

    return (
        <div className="mb-6 flex gap-4 max-xl:flex-col">
            {previousPage ? (
                <PageSwitchLink
                    kind="previous"
                    link={previousLink}
                    group={previousGroup}
                    pageTitle={previousPage.title}
                />
            ) : null}
            {nextPage ? (
                <PageSwitchLink
                    kind="next"
                    link={nextLink}
                    group={nextGroup}
                    pageTitle={nextPage.title}
                />
            ) : null}
        </div>
    );
});

function listOrderedPages(revision: hiveCore.Revision) {
    const pages: hiveCore.RevisionPageDocument[] = [];

    function pushPageDocuments(pageKey: string) {
        const page = revision.pages[pageKey];

        if (page.type === hiveCore.RevisionPageType.Document && !page.hidden) {
            pages.push(page);
        }

        if (
            (page.type === hiveCore.RevisionPageType.Document ||
                page.type === hiveCore.RevisionPageType.Group) &&
            page.pages.length &&
            !page.hidden
        ) {
            page.pages.forEach(pushPageDocuments);
        }
    }

    revision.rootPages.forEach(pushPageDocuments);

    return pages;
}

function getPageClosestPageDocument(
    pages: hiveCore.RevisionPageDocument[],
    pageIndex: number,
    offset: number
): hiveCore.RevisionPageDocument | undefined {
    const candidatePage = pages[pageIndex + offset];
    return candidatePage;
}

function PageSwitchLink(props: { kind: 'previous' | 'next'; link; group; pageTitle }) {
    const { kind, link, group, pageTitle } = props;

    return (
        <NavigationLink
            to={link}
            className={clsx(
                'group/page-switch flex h-full flex-1 items-center justify-between gap-4 rounded border border-page-switch p-4 no-underline transition-all duration-150 hover:translate-y-[-2px] hover:shadow-[rgba(0,0,0,0.02)_0px_12px_13px] focus:border-primary active:border-primary'
            )}
        >
            {kind === 'previous' && <Icon.ArrowLeft size={24} className="text-muted" />}
            <div
                className={clsx(
                    'flex flex-1 flex-col',
                    kind === 'previous' ? 'items-end' : 'items-start'
                )}
            >
                <div className="font-[--sp-typography-ui-base-font-weight] text-muted text-ui-small">
                    {kind === 'previous' ? (
                        <>
                            {group && group.type === hiveCore.RevisionPageType.Group
                                ? `${group.title} - `
                                : ''}
                            Previous
                        </>
                    ) : (
                        <>
                            Next
                            {group && group.type === hiveCore.RevisionPageType.Group
                                ? ` - ${group.title}`
                                : ''}
                        </>
                    )}
                </div>
                <div className="max-w-full truncate text-base text-ui-heading-small group-hover/page-switch:text-primary">
                    {pageTitle}
                </div>
            </div>
            {kind === 'next' && <Icon.ArrowRight size={24} className="text-muted" />}
        </NavigationLink>
    );
}
